import { Preferences } from '@capacitor/preferences'
import { isNativeApp } from './platform'

/**
 * Native B1 — which server ("instance") the store binary talks to (NB1-9, NB1-14).
 *
 * One binary serves every customer: the organizer types an Organization ID, which is a DNS
 * label expanded to `https://<id>.<VITE_NATIVE_PRODUCT_DOMAIN>`. Only the org ID is persisted
 * (Preferences key `orcl.instance` — it is public by construction, it is in DNS); the origin is
 * always re-derived from it, so a tampered stored value can never point requests at an
 * arbitrary host. On web every function here is inert: the page origin is the instance.
 */

/** NB1-9: an org ID is a single lowercase DNS label, max 32 chars. */
export const ORG_ID_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,30}[a-z0-9])?$/

const INSTANCE_KEY = 'orcl.instance'
const DEFAULT_PRODUCT_DOMAIN = 'invalid.example'

export interface Instance {
  orgId: string
  origin: string
}

let current: Instance | null = null

/** NB1-14: build constant; `invalid.example` until the public product name is decided. */
function productDomain(): string {
  const d = (import.meta.env.VITE_NATIVE_PRODUCT_DOMAIN ?? '').trim().toLowerCase()
  return d || DEFAULT_PRODUCT_DOMAIN
}

/**
 * NB1-14 dev escape: a full https origin (the tester box) that every org ID resolves to.
 * Honoured only in a dev build or when the build explicitly opts in with
 * `VITE_NATIVE_ALLOW_DEV_ORIGIN=true` — the release env file must leave that unset (guarded by
 * `instance.test.ts`). Anything that is not a bare https origin is ignored.
 * While it is honoured every org ID maps to the SAME origin, so all org IDs share one credential
 * namespace (`orcl:<dev host>:*`) — dev/tester only, one server (SECURITY.md §2n).
 */
export function devOrigin(): string | null {
  const allowed = import.meta.env.DEV || import.meta.env.VITE_NATIVE_ALLOW_DEV_ORIGIN === 'true'
  if (!allowed) return null
  const raw = (import.meta.env.VITE_NATIVE_DEV_ORIGIN ?? '').trim()
  if (!raw) return null
  try {
    const u = new URL(raw)
    if (u.protocol !== 'https:' || u.username || u.password) return null
    return u.origin
  } catch {
    return null
  }
}

/** Lowercases and trims; null unless the result is a valid org ID (NB1-9). */
export function normalizeOrgId(raw: string): string | null {
  const id = raw.trim().toLowerCase()
  return ORG_ID_PATTERN.test(id) ? id : null
}

export function originForOrgId(orgId: string): string {
  return devOrigin() ?? `https://${orgId}.${productDomain()}`
}

/**
 * The org ID a link points at, or null. https only, no port or userinfo, and the host must be
 * EXACTLY `<label>.<productDomain>` — `ufcw400.<domain>.evil.com` is rejected.
 */
export function orgIdFromUrl(url: string): string | null {
  let u: URL
  try {
    u = new URL(url)
  } catch {
    return null
  }
  if (u.protocol !== 'https:' || u.port || u.username || u.password) return null
  const suffix = `.${productDomain()}`
  const host = u.hostname.toLowerCase()
  if (!host.endsWith(suffix)) return null
  const label = host.slice(0, -suffix.length)
  return ORG_ID_PATTERN.test(label) ? label : null
}

/** Sync read of the hydrated instance; always null on web. */
export function getInstance(): Instance | null {
  if (!isNativeApp()) return null
  return current
}

export async function setInstance(orgId: string): Promise<void> {
  if (!isNativeApp()) return
  const id = normalizeOrgId(orgId)
  if (!id) throw new Error('Invalid organization ID')
  await Preferences.set({ key: INSTANCE_KEY, value: JSON.stringify({ orgId: id }) })
  current = { orgId: id, origin: originForOrgId(id) }
}

export async function clearInstance(): Promise<void> {
  if (!isNativeApp()) return
  await Preferences.remove({ key: INSTANCE_KEY })
  current = null
}

/**
 * Web: same-origin `/api`. Native: the instance's `/api`. Native with no instance also returns
 * the relative `/api`, which resolves against the app shell and fails closed — never a
 * cross-origin request to a guessed server.
 */
export function apiBaseUrl(): string {
  const inst = getInstance()
  return inst ? `${inst.origin}/api` : '/api'
}

/**
 * Origin to build share links / QR codes with. Web: the page; native: the instance. Native with
 * no instance is null, never the app shell's `capacitor://localhost` — a link on that origin
 * opens nothing, so callers show no link / disable the copy or QR action instead.
 */
export function publicOrigin(): string | null {
  if (!isNativeApp()) return window.location.origin
  return getInstance()?.origin ?? null
}

/** Loads the persisted instance into memory. A malformed stored value is treated as none. */
export async function hydrateInstance(): Promise<void> {
  if (!isNativeApp()) return
  current = null
  const { value } = await Preferences.get({ key: INSTANCE_KEY })
  if (!value) return
  try {
    const parsed = JSON.parse(value) as { orgId?: unknown }
    const id = typeof parsed.orgId === 'string' ? normalizeOrgId(parsed.orgId) : null
    if (id) current = { orgId: id, origin: originForOrgId(id) }
  } catch {
    // Malformed — leave unset; the user re-enters the Organization ID.
  }
}
